/**
 * The pure decisions inside `SubscriptionManagerService`'s Paddle paths (#4189),
 * extracted so they can be unit-tested.
 *
 * The service itself still cannot be loaded by the mocha harness (its Angular DI
 * import chain pulls in the whole app), which is why these live in their own
 * file rather than being tested through the service. That limitation was
 * acceptable while the Paddle arms were one-line dispatches; they are not any
 * more, so the parts that actually decide something moved here.
 */

/** Billing rails the subscription manager dispatches on. `null` means unknown. */
export type BillingProviderName = 'payPro' | 'paddle' | 'googlePlay' | 'apple' | null;

/**
 * Whether a Resume should be dispatched to Paddle.
 *
 * The second arm is the load-bearing one. `provider` is populated only from the
 * `getSubscriptionInfo` response, so a transient failure leaves it null — and
 * the plain `provider === 'paddle'` test would then fall through to "no
 * subscription to resume". The profile page deliberately still offers Resume in
 * that state, because a taken-effect pause is known from local session data
 * alone. Without this arm the button would be present and do nothing, which is
 * the in-app dead end the whole paused-handling feature exists to prevent.
 *
 * Takes the paused fact as a plain boolean rather than the session document, so
 * the rule stays independent of how "is this subscriber paused" is determined.
 */
export function shouldDispatchPaddleResume(
  provider: BillingProviderName,
  isPausedSubscriber: boolean,
): boolean {
  return provider === 'paddle'
    || (provider === null && isPausedSubscriber);
}

/**
 * How many months the subscriber's CURRENT Paddle plan bills for.
 *
 * Paddle personal plans come in exactly one pair, monthly and yearly, so when
 * the server told us the switch TARGET we know the current plan is the other
 * one. That inversion is preferred over the plan's name because the name is
 * English text from the server, whereas the target's period is structured data.
 *
 * The name is the fallback for the case where no switch target came back (an
 * unrecognised price pair, or an environment whose ids we do not map). It is the
 * only signal left at that point.
 */
export function resolvePaddleCurrentPlanMonths(
  switchTargetMonths: number | undefined,
  planName: string,
): number {
  if (switchTargetMonths !== undefined) {
    // the complement of the target: if they can switch TO yearly they are on
    // monthly, and the other way round.
    return switchTargetMonths === 12 ? 1 : 12;
  }
  return planName === 'Yearly' ? 12 : 1;
}
